/**
 * UserJourneyGuideContext - Backend-backed user journey guide settings
 * Fetches from API, caches locally, syncs writes to backend
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  getUserJourneyGuideSettings,
  updateUserJourneyGuideSettings,
  resetUserJourneyGuideSettings as apiResetSettings
} from '../services/userJourneyGuideApi';

const UserJourneyGuideContext = createContext(null);

export const useUserJourneyGuide = () => {
  const ctx = useContext(UserJourneyGuideContext);
  if (!ctx) {
    throw new Error('useUserJourneyGuide must be used within UserJourneyGuideProvider');
  }
  return ctx;
};

// Some embedded surfaces (e.g. off-screen export, chat embeds) render outside the main provider tree.
// They should not hard-crash when UserJourneyGuideProvider is absent.
export const useUserJourneyGuideOptional = () => useContext(UserJourneyGuideContext) || {};

export const UserJourneyGuideProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    const token = localStorage.getItem('ids_token');
    if (!token) {
      setLoaded(true);
      return;
    }
    try {
      const res = await getUserJourneyGuideSettings();
      
      // // Check for invalid credentials response
      // if (res && res.status === false && 
      //     res.msg === "Invalid Credentials, Please try logging out and login again") {
      //   console.warn('[UserJourneyGuideContext] Invalid credentials detected, triggering auto-logout');
      //   window.dispatchEvent(new CustomEvent('autoLogout'));
      //   return;
      // }
      
      if (res?.status && res?.data) {
        // Merge only defined values from API so we don't overwrite journey flags (e.g. from resetJourney) when API omits or returns undefined
        const fromApi = Object.fromEntries(
          Object.entries(res.data).filter(([, v]) => v !== undefined && v !== null)
        );
        setSettings(prev => ({ ...prev, ...fromApi }));
        console.log('[UserJourneyGuideContext] Fetched settings (journey flags):', {
          ids_should_show_guide: res.data.ids_should_show_guide,
          ids_force_show_workspace_guide: res.data.ids_force_show_workspace_guide,
          ids_workspace_guide_dismissed: res.data.ids_workspace_guide_dismissed || '(empty)'
        });
      }
      // Merge with localStorage fallback for ids_should_show_guide (from login)
      const lsShouldShow = localStorage.getItem('ids_should_show_guide');
      if (lsShouldShow !== null && !(res?.data && 'ids_should_show_guide' in res.data)) {
        setSettings(prev => ({ ...prev, ids_should_show_guide: lsShouldShow }));
      }
    } catch (err) {
      console.error('Failed to fetch user journey guide settings', err);
      // Check for 401 Unauthorized error
        if (err.response?.status === 401) {
          console.warn('[UserJourneyGuideContext] 401 Unauthorized error detected, triggering auto-logout');
          window.dispatchEvent(new CustomEvent('autoLogout'));
          return;
        }
      // Fallback: populate from localStorage for migration
      const fallback = {};
      const keys = [
        'ids_should_show_guide', 'ids_force_show_workspace_guide', 'ids_workspace_guide_dismissed',
        'ids_discovery_template_guide_dismissed', 'ids_discovery_phase5_approval_guide_dismissed',
        'ids_discovery_phase6_show_guide', 'ids_discovery_phase6_workspace_id'
      ];
      keys.forEach(k => {
        const v = localStorage.getItem(k);
        if (v !== null) fallback[k] = v;
      });
      setSettings(fallback);
      console.log('[UserJourneyGuideContext] Using fallback settings (journey flags):', {
        ids_should_show_guide: fallback.ids_should_show_guide,
        ids_force_show_workspace_guide: fallback.ids_force_show_workspace_guide,
        ids_workspace_guide_dismissed: fallback.ids_workspace_guide_dismissed || '(empty)'
      });
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const onRefresh = () => fetchSettings();
    const onClear = () => setSettings({});
    window.addEventListener('userJourneyRefresh', onRefresh);
    window.addEventListener('userJourneyClear', onClear);
    return () => {
      window.removeEventListener('userJourneyRefresh', onRefresh);
      window.removeEventListener('userJourneyClear', onClear);
    };
  }, [fetchSettings]);

  const getSetting = useCallback((key) => {
    return settings[key] ?? '';
  }, [settings]);

  const setSetting = useCallback(async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    try {
      const res = await updateUserJourneyGuideSettings({ [key]: value });
      if (res && res.status === false && 
          res.msg === "Invalid Credentials, Please try logging out and login again") {
        console.warn('[UserJourneyGuideContext] Invalid credentials detected in setSetting, triggering auto-logout');
        window.dispatchEvent(new CustomEvent('autoLogout'));
      }
    } catch (err) {
      console.error('Failed to update user journey guide setting', err);
    }
  }, []);

  const setSettingsBulk = useCallback(async (updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
    try {
      const res = await updateUserJourneyGuideSettings(updates);
      if (res && res.status === false && 
          res.msg === "Invalid Credentials, Please try logging out and login again") {
        console.warn('[UserJourneyGuideContext] Invalid credentials detected in setSettingsBulk, triggering auto-logout');
        window.dispatchEvent(new CustomEvent('autoLogout'));
      }
    } catch (err) {
      console.error('Failed to update user journey guide settings', err);
    }
  }, []);

  const removeSetting = useCallback(async (key) => {
    setSettings(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      const res = await updateUserJourneyGuideSettings({ [key]: '' });
      if (res && res.status === false && 
          res.msg === "Invalid Credentials, Please try logging out and login again") {
        console.warn('[UserJourneyGuideContext] Invalid credentials detected in removeSetting, triggering auto-logout');
        window.dispatchEvent(new CustomEvent('autoLogout'));
      }
    } catch (err) {
      console.error('Failed to remove user journey guide setting', err);
    }
  }, []);

  const resetJourney = useCallback(async () => {
    try {
      await apiResetSettings();
      const updates = {
        ids_should_show_guide: 'true',
        ids_force_show_workspace_guide: 'true'
      };
      await updateUserJourneyGuideSettings(updates);
      setSettings(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Failed to reset user journey', err);
      throw err;
    }
  }, []);

  const value = {
    settings,
    loaded,
    getSetting,
    setSetting,
    setSettingsBulk,
    removeSetting,
    resetJourney,
    refetch: fetchSettings
  };

  return (
    <UserJourneyGuideContext.Provider value={value}>
      {children}
    </UserJourneyGuideContext.Provider>
  );
};
